import {useEffect, useState} from 'react';
import {
  view,
  Action,
  Icon,
  useCollapsible,
  assignTokens,
  tokens,
} from '@sail/ui';
import {CollapsibleContainer, css} from '@sail/ui/deprecated';
import {defineMessages, useIntl} from 'react-intl';
import {StripeAssistantLiveAnswersSource} from '@stripe-internal/conversations-client';
import {DotComSourceHostGroup} from './DotComSourceHostGroup';
import {useAnalytics} from '../../../AnalyticsProvider';
import {dotComColors} from '../../../theme';

const messages = defineMessages({
  sources: {
    id: 'conversations_ui.mse_inline_data.universal_help_live_answers_response.dot_com_sources_dropdown.sources',
    description: 'Label to expand and collapse live answers sources dropdown',
    defaultMessage:
      '{sourcesCount, plural, one {Summarized from # source} other {Summarized from # sources}}',
  },
});

type SourceHostGroup = {
  host: string;
  sources: StripeAssistantLiveAnswersSource[];
};

const getSourceHostGroups = (
  sources: StripeAssistantLiveAnswersSource[],
): SourceHostGroup[] => {
  const groups: Record<string, StripeAssistantLiveAnswersSource[]> = {};

  sources.forEach((source) => {
    const {host} = new URL(source.url);

    if (groups[host]) {
      groups[host].push(source);
    } else {
      groups[host] = [source];
    }
  });

  const sourceHostGroups = Object.entries(groups).map(
    ([host, hostSources]) => ({
      host,
      sources: hostSources,
    }),
  );

  return sourceHostGroups;
};

export const DotComSourcesDropdown = ({
  sources,
}: {
  sources: Array<StripeAssistantLiveAnswersSource>;
}) => {
  const analytics = useAnalytics();
  const intl = useIntl();

  const [isOpen, setOpen] = useState(false);
  const [markedOpen, setMarkedOpen] = useState(false);
  const {triggerProps, collapsibleProps, icon} = useCollapsible({
    open: isOpen,
    iconOrientation: 'right-down',
  });

  useEffect(() => {
    if (isOpen) {
      analytics.action(
        'universal_help_live_answers_frontend_sources_opened',
        {},
      );
      setMarkedOpen(true);
    } else if (markedOpen) {
      analytics.action(
        'universal_help_live_answers_frontend_sources_closed',
        {},
      );
      setMarkedOpen(false);
    }
  }, [isOpen, analytics, markedOpen]);

  const sourceHostGroups = getSourceHostGroups(sources);

  return (
    <view.div>
      <Action
        onPress={() => {
          setOpen(!isOpen);
        }}
        {...triggerProps}
        uses={[
          assignTokens(tokens.color.text.action.primary, dotComColors.brand600),
          assignTokens(tokens.color.icon.action.primary, dotComColors.brand600),
        ]}
        css={{
          stack: 'x',
          alignX: 'start',
          alignY: 'center',
          gap: 'small',
          color: 'action.primary',
          font: 'label.small.emphasized',
          paddingTop: 'xsmall',
        }}
      >
        <Icon icon={icon} size="xxsmall" css={{color: 'inherit'}} />
        {intl.formatMessage(messages.sources, {sourcesCount: sources.length})}
      </Action>
      <CollapsibleContainer
        {...collapsibleProps}
        css={{
          width: css.value('calc(100% + 60px)'),
        }}
      >
        <view.div
          css={{
            stack: 'y',
            gap: 'small',
            paddingY: 'small',
          }}
        >
          {sourceHostGroups.map(({host, sources: hostSources}) => (
            <DotComSourceHostGroup
              key={host}
              host={host}
              sources={hostSources}
            />
          ))}
        </view.div>
      </CollapsibleContainer>
    </view.div>
  );
};
